import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/shared/services/api.service';
import { FilterSettings } from '../../projects/project-filters/filter-settings';
import { CommentView } from 'src/app/shared/data/data.generated';
import { ApplicationFeature } from 'src/app/shared/data/application-feature';
import { cleanDescription, formatDate, getActivityIcon, getResponsiblePartyColor, getResponsiblePartyName, getSourceName, getStatusColor, getStatusName, shippedColor } from '../shared';

type CalendarWeekGroup = {
  year: number;
  weekNumber: number;
  dates: Date[];
};

type CalendarYearGroup = {
  year: number;
  span: number;
};

type CalendarDayColumn = {
  date: Date;
  dayNumber: string;
  isFirstOfMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
};

type CalendarDayCell = {
  date: Date;
  isFirstOfMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
  style: string;
};

@Component({
  selector: 'master-schedule-calendar',
  templateUrl: './master-schedule-calendar.component.html',
  styleUrl: './master-schedule-calendar.component.scss',
  standalone: false
})
export class MasterScheduleCalendarComponent implements AfterViewInit, OnDestroy {

  constructor(private readonly api: ApiService) { }

  public getStatusColor = getStatusColor;
  public getStatusName = getStatusName;
  public shippedColor = shippedColor;
  public getResponsiblePartyColor = getResponsiblePartyColor;
  public getActivityIcon = getActivityIcon;
  public getResponsiblePartyName = getResponsiblePartyName;
  public getSourceName = getSourceName;
  
  public filterMenuVisible: boolean = false;
  public filterMenuPosition = { x: 0, y: 0 };
  public weekGroups: CalendarWeekGroup[] = [];
  public yearGroups: CalendarYearGroup[] = [];
  public dayColumns: CalendarDayColumn[] = [];
  public visibleActivities: any[] = [];
  public showProjectActivityColumn: boolean = false;
  public dateSelectorMinDate: Date | null = null;
  public dateSelectorMaxDate: Date | null = null;
  public dateSelectorSelectedDate: Date | null = null;
  public weeklyProjectDetails: { [projectNumber: string]: any } = {};
  public weeklyProjectDetailsLoading: { [projectNumber: string]: boolean } = {};
  public weeklyProjectDetailsVisible: boolean = false;
  public weeklyProjectDetailsData: any = null;
  public weeklyProjectDetailsPosition = { x: 0, y: 0 };
  @ViewChild('calendarViewportWrapper') public calendarViewportWrapper: ElementRef<HTMLDivElement> | null = null;
  @ViewChild('calendarViewport') public calendarViewport: ElementRef<HTMLDivElement> | null = null;

  public viewportHeightPx: number | null = null;

  private resizeObserver: ResizeObserver | null = null;
  private readonly autoDayCellWidth = 25;
  private readonly staticActivityColumnWidth = 285;
  private readonly staticProjectNumberColumnWidth = 160;
  private readonly staticWeekProjectNumberColumnWidth = 240;
  private readonly staticItemDescriptionColumnWidth = 270;
  private readonly staticItemActivityColumnWidth = 132.5;

  @Input() public showNewWindowIcon: boolean = false;
  @Input() public projectNumberClickEnabled: boolean = true;
  @Input() public filterSettings: FilterSettings = new FilterSettings();
  @Input() public initialZoomLevel: number = 1.0;
  @Input() public firstDateLeadInDays: number = 1;
  @Input() public updateProjects: (() => void) | null = null;
  @Input() set lastUpdated(timeStamp: Date) {
    this.loadProjectActivities();
  }
  public canEdit: boolean = false;
  public isLoading: boolean = true;
  public activities: any[] = [];
  public count: number = 0;
  public page: number = 1;
  public pageSize: number = 99;
  public pages: number = 0;
  public holidays: any[] = [];
  @Input() public project: string = "";
  @Input() public projectData: any = null;
  @Input() public activityName: string = "";

  public selectionRowId: number | null = null;
  public selectionStartDate: Date | null = null;
  public selectionEndDate: Date | null = null;

  public updating: boolean = false;
  public lastBackendUpdate: Date | null = null;
  public backendCheckVisible: boolean = false;

  public contextRowId: number | null = null;
  public contextMenuVisible: boolean = false;
  public contextMenuPosition = { x: 0, y: 0 };
  public contextActivity: any = null;
  public overrideExpectedDateVisible: boolean = false;
  public overrideExpectedDateActivity: any = null;
  public overrideExpectedStartDate: string = '';
  public overrideExpectedEndDate: string = '';
  public overrideExpectedDateComment: string = '';

  public duplicateDatesActivityIds: number[] = [];
  public duplicateDatesVisible: boolean = false;
  public duplicateDatesSelectedProjectIds: number[] = [];
  public duplicateDatesSelectionLimit: number = 25;

  public activityHistoryVisible: boolean = false;
  public activityHistoryData: any[] = [];

  public dateShifterVisible: boolean = false;
  public dateShifterActivities: any[] = [];
  public dateShifterPosition = { x: 0, y: 0 };
  public dateShiftDays: number = 0;

  public showNotes: boolean = false;
  public notesActivity: any = null;
  public notes: CommentView[] = [];
  public noteType: string = 'MasterScheduleActivity';
  public singleProjectModalVisible: boolean = false;
  public singleProjectModalProject: string = '';

  // Zoom state for calendar viewport
  public zoomLevel: number = 1.0; // 1.0 = 100%
  public zoomOrigin: string = '0 0'; // transform-origin (left top)


  ngOnInit(): void {
    this.zoomLevel = Math.max(0.5, Math.min(2.0, this.initialZoomLevel || 1.0));
    this.canEdit = this.api.CanEditFeature(ApplicationFeature.MasterSchedule);
    this.loadProjectActivities(() => {
      let startAtToday = this.filterSettings.startAtToday;
      this.filterSettings.startAtToday = false;
      this.startAtChanged();
      this.filterSettings.startAtToday = startAtToday;
      this.startAtChanged();
    });
    this.api.MasterSchedule.GetMasterScheduleHolidays().subscribe((holidays: any[]) => {
      this.holidays = holidays;
      this.holidays.forEach(holiday => {
        holiday.Date = new Date(holiday.Date);
      });
    });
    this.filterSettings.dayRangeChanged = this.dayRangeChanged.bind(this);
    this.filterSettings.startAtChanged = this.startAtChanged.bind(this);
  }

  ngAfterViewInit(): void {
    this.recalculateDaysShownFromTableWidth();
    this.updateViewportHeight();

    const wrapper = this.calendarViewportWrapper?.nativeElement;
    const viewport = this.calendarViewport?.nativeElement;
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.recalculateDaysShownFromTableWidth();
        this.updateViewportHeight();
        this.dayRangeChanged();
      });
      if (wrapper) {
        this.resizeObserver.observe(wrapper);
      }
      if (viewport) {
        this.resizeObserver.observe(viewport);
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.recalculateDaysShownFromTableWidth();
    this.updateViewportHeight();
    this.dayRangeChanged();
  }

  loadProjectActivities(postLoadCallback: (() => void) | null = null) {
    this.isLoading = true;
    this.api.MasterSchedule.GetMasterScheduleActivities(this.project, this.page, this.pageSize, this.filterSettings).subscribe((result: any) => {
      this.activities = result.Data;
      this.count = result.Count;
      this.pages = Math.ceil(this.count / this.pageSize);
      this.activities.forEach(activity => {
        if (activity.ExpectedStartDate)
          activity.ExpectedStartDate = this.toDateOnly(activity.ExpectedStartDate);
        if (activity.ExpectedEndDate)
          activity.ExpectedEndDate = this.toDateOnly(activity.ExpectedEndDate);
        if (activity.ActualStartDate)
          activity.ActualStartDate = this.toDateOnly(activity.ActualStartDate);
        if (activity.ActualEndDate)
          activity.ActualEndDate = this.toDateOnly(activity.ActualEndDate);
        if (activity.ExpectedStartDate?.getFullYear() < 2000)
          activity.ExpectedStartDate = null;
        if (activity.ExpectedEndDate?.getFullYear() < 2000)
          activity.ExpectedEndDate = null;
        if (activity.ActualStartDate?.getFullYear() < 2000)
          activity.ActualStartDate = null;
        if (activity.ActualEndDate?.getFullYear() < 2000)
          activity.ActualEndDate = null;
        activity.ItemDescription = this.cleanDescription(activity.ItemDescription);
      });
      this.lastBackendUpdate = new Date();
      if (postLoadCallback) {
        postLoadCallback();
      }
      else {
        this.startAtChanged();
      }
      this.updateDateSelectorRange();
      this.dayRangeChanged();
      this.isLoading = false;
      this.scheduleViewportHeightUpdate();
    });
  }

  dayRangeChanged() {
    this.recalculateDaysShownFromTableWidth();
    const rawStartDate = this.filterSettings.startDate as unknown;
    let normalizedStartDate = rawStartDate instanceof Date ? rawStartDate : new Date(rawStartDate as string);
    if (Number.isNaN(normalizedStartDate.getTime())) {
      normalizedStartDate = new Date();
      normalizedStartDate.setHours(0, 0, 0, 0);
      normalizedStartDate.setDate(normalizedStartDate.getDate() - 1);
    }
    this.filterSettings.startDate = normalizedStartDate;

    const selectedOffsetDays = this.filterSettings.showWeeks
      ? this.filterSettings.selectedDay * 7
      : this.filterSettings.selectedDay;
    this.dateSelectorSelectedDate = this.toDateOnly(
      this.addDays(normalizedStartDate, selectedOffsetDays)
    );

    let rangeStartDate = new Date(normalizedStartDate.getTime());
    const offsetDays = this.filterSettings.showWeeks ? this.filterSettings.selectedDay * 7 : this.filterSettings.selectedDay;
    rangeStartDate.setDate(rangeStartDate.getDate() + offsetDays);
    const rangeCount = this.filterSettings.showWeeks ? this.filterSettings.daysShown * 7 : this.filterSettings.daysShown;
    if (this.filterSettings.showWeeks) {
      rangeStartDate.setDate(rangeStartDate.getDate() - Math.floor(rangeCount / 2));
    }
    this.filterSettings.dates = this.getDateRange(rangeStartDate, rangeCount);
    this.updateCalendarGroups();
    this.settingChanged();
    this.scheduleViewportHeightUpdate();
  }

  viewModeChanged() {
    this.updateRangeBounds();
    this.updateDateSelectorRange();
    this.recalculateDaysShownFromTableWidth();
    this.dayRangeChanged();
    this.scheduleViewportHeightUpdate();
  }

  private scheduleViewportHeightUpdate() {
    requestAnimationFrame(() => this.updateViewportHeight());
  }

  private updateViewportHeight() {
    const viewport = this.calendarViewport?.nativeElement;
    if (!viewport) {
      return;
    }

    this.viewportHeightPx = Math.ceil(viewport.offsetHeight * this.zoomLevel) + 5;
  }

  private recalculateDaysShownFromTableWidth() {
    const tableWidth = this.calendarViewportWrapper?.nativeElement?.clientWidth ?? 0;
    if (tableWidth <= 0) {
      return;
    }

    const effectiveVisibleWidth = tableWidth / this.zoomLevel;

    const staticColumnsWidth = this.filterSettings.activity
      ? (this.filterSettings.showWeeks ? this.staticWeekProjectNumberColumnWidth : this.staticProjectNumberColumnWidth) +
        (this.filterSettings.showWeeks ? 0 : this.staticItemDescriptionColumnWidth) +
        (this.hasMultiActivityProjectsInCurrentView() ? this.staticItemActivityColumnWidth : 0)
      : this.staticActivityColumnWidth;

    const computedDaysShown = Math.max(1, Math.floor((effectiveVisibleWidth - staticColumnsWidth) / this.autoDayCellWidth));
    this.filterSettings.daysShown = computedDaysShown;
  }

  updateRangeBounds() {
    if (this.filterSettings.showWeeks) {
      this.filterSettings.dayRangeMin = -104;
      this.filterSettings.dayRangeMax = 104;
    } else {
      this.filterSettings.dayRangeMin = -180;
      this.filterSettings.dayRangeMax = 360;
    }

    this.filterSettings.selectedDay = Math.min(
      Math.max(this.filterSettings.selectedDay, this.filterSettings.dayRangeMin),
      this.filterSettings.dayRangeMax
    );
  }

  private updateDateSelectorRange() {
    const firstActivityDate = this.getFirstActivityDate();
    if (!firstActivityDate) {
      this.dateSelectorMinDate = null;
      this.dateSelectorMaxDate = null;
      this.dateSelectorSelectedDate = null;
      return;
    }

    if (this.filterSettings.showWeeks) {
      const today = this.toDateOnly(new Date());
      this.dateSelectorMinDate = this.addYears(today, -2);
      this.dateSelectorSelectedDate = new Date(today.getTime());
      this.dateSelectorMaxDate = this.addYears(today, 2);
    } else {
      this.dateSelectorMinDate = this.addDays(firstActivityDate, -3);
      this.dateSelectorSelectedDate = new Date(this.dateSelectorMinDate.getTime());
      const oneYearAfterStart = this.addYears(this.dateSelectorMinDate, 1);
      const maxActivityEndDate = this.getMaxActivityEndDate();
      const oneMonthAfterMaxEnd = maxActivityEndDate
        ? this.addMonths(maxActivityEndDate, 1)
        : oneYearAfterStart;
      this.dateSelectorMaxDate = oneMonthAfterMaxEnd > oneYearAfterStart
        ? oneMonthAfterMaxEnd
        : oneYearAfterStart;
    }

    this.filterSettings.startDate = new Date(this.dateSelectorMinDate.getTime());
    this.filterSettings.dayRangeMin = 0;
    this.filterSettings.dayRangeMax = this.getDateOffsetInCalendarUnits(
      this.dateSelectorMaxDate,
      this.dateSelectorMinDate
    );
    this.filterSettings.selectedDay = this.getDateOffsetInCalendarUnits(
      this.dateSelectorSelectedDate,
      this.dateSelectorMinDate
    );
  }

  private getFirstActivityDate(): Date | null {
    const dates = this.activities
      .map(activity => activity.ExpectedStartDate)
      .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()));

    if (dates.length === 0) {
      return null;
    }

    return new Date(Math.min(...dates.map(date => date.getTime())));
  }

  private getMaxActivityEndDate(): Date | null {
    const dates = this.activities
      .flatMap(activity => [activity.ExpectedEndDate, activity.ActualEndDate])
      .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()));

    if (dates.length === 0) {
      return null;
    }

    return new Date(Math.max(...dates.map(date => date.getTime())));
  }

  private getDateOffsetInCalendarUnits(date: Date, startDate: Date): number {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const days = Math.round((date.getTime() - startDate.getTime()) / millisecondsPerDay);
    return this.filterSettings.showWeeks ? Math.round(days / 7) : days;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
  }

  private addMonths(date: Date, months: number): Date {
    const result = new Date(date.getTime());
    result.setMonth(result.getMonth() + months);
    return result;
  }

  private addYears(date: Date, years: number): Date {
    const result = new Date(date.getTime());
    result.setFullYear(result.getFullYear() + years);
    return result;
  }

  dateSelectorChanged(selectedDate: Date | null) {
    if (!selectedDate || !this.dateSelectorMinDate) {
      return;
    }

    this.dateSelectorSelectedDate = selectedDate;
    this.filterSettings.startDate = new Date(this.dateSelectorMinDate.getTime());
    this.filterSettings.selectedDay = this.getDateOffsetInCalendarUnits(
      selectedDate,
      this.dateSelectorMinDate
    );
    this.dayRangeChanged();
  }

  updateCalendarGroups() {
    if (this.filterSettings.showWeeks) {
      this.buildWeekGroups();
      this.filterSettings.months = [];
      this.dayColumns = [];
      return;
    }

    this.filterSettings.months = this.filterSettings.dates
      .map(date => this.getFullMonthName(date))
      .filter((value, index, self) => self.indexOf(value) === index);
    this.weekGroups = [];
    this.yearGroups = [];
  }

  buildWeekGroups() {
    const weekMap = new Map<string, CalendarWeekGroup>();
    for (const date of this.filterSettings.dates) {
      const weekInfo = this.getIsoWeekInfo(date);
      const key = `${weekInfo.year}-${weekInfo.weekNumber}`;
      const existing = weekMap.get(key);
      if (existing) {
        existing.dates.push(date);
      } else {
        weekMap.set(key, {
          year: weekInfo.year,
          weekNumber: weekInfo.weekNumber,
          dates: [date]
        });
      }
    }

    this.weekGroups = Array.from(weekMap.values());
    this.yearGroups = [];

    for (const weekGroup of this.weekGroups) {
      const currentYearGroup = this.yearGroups[this.yearGroups.length - 1];
      if (currentYearGroup && currentYearGroup.year === weekGroup.year) {
        currentYearGroup.span++;
      } else {
        this.yearGroups.push({ year: weekGroup.year, span: 1 });
      }
    }
  }

  getIsoWeekInfo(date: Date): { year: number; weekNumber: number } {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayOfWeek = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayOfWeek);
    const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { year: utcDate.getUTCFullYear(), weekNumber };
  }

  getDateSliderLabel(): string {
    return this.filterSettings.showWeeks ? 'Weeks' : 'Days';
  }

  settingChanged() {
    this.refreshRenderedActivities();
    localStorage.setItem("masterScheduleFilterSettings", JSON.stringify(this.filterSettings));
  }

  private getProjectGroupKey(activity: any): string {
    return `${activity?.ProjectNumber ?? ''}::${activity?.ItemDescription ?? ''}`;
  }

  shouldShowProjectInfo(activity: any): boolean {
    if (!this.filterSettings.activity) {
      return false;
    }
    return !!activity?.__showProjectInfo;
  }

  getProjectDescriptionDisplay(activity: any): string {
    const itemDescription = activity?.ItemDescription ?? '';
    const isFirstProjectRow = !!activity?.__showProjectInfo;

    return isFirstProjectRow ? itemDescription : '';
  }

  getProjectActivityDisplay(activity: any): string {
    const projectActivityCount = activity?.__projectActivityCount ?? 1;
    if (projectActivityCount <= 1) {
      return '';
    }
    return activity?.Name ?? '';
  }

  shouldShowProjectSeparator(index: number): boolean {
    if (!this.filterSettings.activity || !this.showProjectActivityColumn || index <= 0) {
      return false;
    }

    const currentActivity = this.visibleActivities[index];
    const previousActivity = this.visibleActivities[index - 1];
    if (!currentActivity || !previousActivity) {
      return false;
    }

    return this.getProjectGroupKey(currentActivity) !== this.getProjectGroupKey(previousActivity);
  }

  private hasMultiActivityProjectsInCurrentView(): boolean {
    if (!this.filterSettings.activity) {
      return false;
    }

    const visible = this.activities.filter(activity => this.isVisible(activity));
    const counts = new Map<string, number>();
    for (const activity of visible) {
      const key = this.getProjectGroupKey(activity);
      const nextCount = (counts.get(key) ?? 0) + 1;
      if (nextCount > 1) {
        return true;
      }
      counts.set(key, nextCount);
    }

    return false;
  }

  private refreshRenderedActivities() {
    this.visibleActivities = this.activities.filter(activity => this.isVisible(activity));

    const projectActivityCounts = new Map<string, number>();
    for (const activity of this.visibleActivities) {
      const key = this.getProjectGroupKey(activity);
      projectActivityCounts.set(key, (projectActivityCounts.get(key) ?? 0) + 1);
    }

    const seenProjectKeys = new Set<string>();
    for (const activity of this.visibleActivities) {
      const key = this.getProjectGroupKey(activity);
      activity.__projectActivityCount = projectActivityCounts.get(key) ?? 1;
      activity.__showProjectInfo = !seenProjectKeys.has(key);
      seenProjectKeys.add(key);
    }

    this.showProjectActivityColumn = Array.from(projectActivityCounts.values()).some(count => count > 1);

    if (this.filterSettings.showWeeks) {
      return;
    }

    this.dayColumns = this.filterSettings.dates.map(date => ({
      date,
      dayNumber: this.getDayNumber(date),
      isFirstOfMonth: this.isFirstOfMonth(date),
      isToday: this.isToday(date),
      isWeekend: !this.isWorkDay(null, date),
    }));

    for (const activity of this.visibleActivities) {
      activity.dayCells = this.dayColumns.map(dayColumn => this.buildDayCell(activity, dayColumn));
    }
  }

  private buildDayCell(activity: any, dayColumn: CalendarDayColumn): CalendarDayCell {
    return {
      date: dayColumn.date,
      isFirstOfMonth: dayColumn.isFirstOfMonth,
      isToday: dayColumn.isToday,
      isWeekend: !this.isWorkDay(activity, dayColumn.date),
      isHoliday: this.isHoliday(activity, dayColumn.date),
      style: this.buildActivityCellStyle(activity, dayColumn.date),
    };
  }

  private buildActivityCellStyle(activity: any, date: Date): string {
    if (!this.isActivityOnDate(activity, date)) {
      return '';
    }

    const staticStyle = '; height: 100%; ';
    const lightColor = this.getResponsiblePartyColor(activity.ResponsiblePartyId, true);
    const darkColor = this.getResponsiblePartyColor(activity.ResponsiblePartyId, false);
    if (!activity.IsConfirmed) {
      return 'background: ' + lightColor + staticStyle;
    }

    let topColor = '#00000000';
    let bottomColor = '#00000000';
    if (date > new Date(activity.ExpectedEndDate)) {
      topColor = this.isWarning(activity, date) ? '#fff126ff' : '#FF0000';
    } else {
      topColor = lightColor;
    }

    if (date >= new Date(activity.ActualStartDate) && date <= new Date(activity.ActualEndDate)) {
      bottomColor = darkColor;
    }

    return `background: linear-gradient(
      to bottom,
      ${topColor} 0%,
      ${topColor} 33.33%,
      ${bottomColor} 33.33%,
      ${bottomColor} 100% /*
    )` + staticStyle;
  }

  getDateRange(startDate: Date, days: number): Date[] {
    const dates: Date[] = [];
    let currentDate = new Date(startDate.getTime());
    currentDate.setHours(0, 0, 0, 0);
    for (let i = 0; i < days; i++) {
      if (!this.filterSettings.showWeekends && (currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
        days++;
      } else if (!this.filterSettings.showFridays && currentDate.getDay() === 5) {
        days++;
      }
      else {
        dates.push(new Date(currentDate.getTime()));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }

  startAtChanged() {
    if (this.filterSettings.startAtToday) {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      this.filterSettings.startDate = today;
      this.filterSettings.selectedDay = 0;
      this.dayRangeChanged();
    }
    else {
      // Start at min activity start date
      let earliestDate: Date | null = null;
      for (let activity of this.activities) {
        if (activity.ExpectedStartDate) {
          let activityStartDate = new Date(activity.ExpectedStartDate);
          if (earliestDate === null || activityStartDate < earliestDate) {
            earliestDate = activityStartDate;
          }
        }
      }
      if (earliestDate) {
        const leadInDays = Math.max(0, Math.floor(Number(this.firstDateLeadInDays) || 0));
        earliestDate.setDate(earliestDate.getDate() - leadInDays);
        this.filterSettings.startDate = earliestDate;
        this.filterSettings.selectedDay = 0;
      }
      this.dayRangeChanged();
    }
  }

  isActivityOnDate(activity: any, date: Date): boolean {
    return (date >= new Date(activity.ActualStartDate) &&
      date <= new Date(activity.ActualEndDate)) ||
      (date >= new Date(activity.ExpectedStartDate) &&
        date <= new Date(activity.ExpectedEndDate));
  }

  private toDateOnly(value: Date | string): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  getMonthColumnSpan(month: string): number {
    return this.filterSettings.dates.filter(d => this.getFullMonthName(d) === month).length;
  }

  getFullMonthName(date: Date): string {
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }

  getDayNumber(date: Date): string {
    return date.getDate().toString();
  }

  isActivityInWeek(activity: any, weekGroup: CalendarWeekGroup): boolean {
    return weekGroup.dates.some(date => this.isActivityOnDate(activity, date));
  }

  isWeekToday(weekGroup: CalendarWeekGroup): boolean {
    return weekGroup.dates.some(date => this.isToday(date));
  }

  isWeekHoliday(activity: any, weekGroup: CalendarWeekGroup): boolean {
    return weekGroup.dates.some(date => this.isHoliday(activity, date));
  }

  isFirstWeekOfYear(weekGroup: CalendarWeekGroup): boolean {
    const weekIndex = this.weekGroups.findIndex(w => w.year === weekGroup.year && w.weekNumber === weekGroup.weekNumber);
    if (weekIndex <= 0) {
      return weekIndex === 0;
    }

    return this.weekGroups[weekIndex - 1].year !== weekGroup.year;
  }

  showWeeklyProjectDetails(projectNumber: string, event: MouseEvent) {
    event.stopPropagation();
    this.weeklyProjectDetailsPosition = { x: event.clientX + 10, y: event.clientY + 10 };
    this.weeklyProjectDetailsVisible = true;
    this.weeklyProjectDetailsData = this.weeklyProjectDetails[projectNumber] ?? null;

    if (this.weeklyProjectDetails[projectNumber] || this.weeklyProjectDetailsLoading[projectNumber]) {
      return;
    }

    this.weeklyProjectDetailsLoading[projectNumber] = true;
    this.api.MasterSchedule.GetMasterScheduleProject(projectNumber).subscribe({
      next: project => {
        project.ItemDescription = cleanDescription(project.ItemDescription);
        project.PoDate = formatDate(project.PoDate);
        project.RequestedShipDate = formatDate(project.RequestedShipDate);
        project.AcknowledgedShipDate = formatDate(project.AcknowledgedShipDate);
        project.CurrentShipDate = formatDate(project.CurrentShipDate);
        project.ApprovalDrawingDate = formatDate(project.ApprovalDrawingDate);
        this.weeklyProjectDetails[projectNumber] = project;
        this.weeklyProjectDetailsData = project;
      },
      complete: () => {
        this.weeklyProjectDetailsLoading[projectNumber] = false;
      },
      error: () => {
        this.weeklyProjectDetailsLoading[projectNumber] = false;
      }
    });
  }

  hideWeeklyProjectDetails() {
    this.weeklyProjectDetailsVisible = false;
    this.weeklyProjectDetailsData = null;
  }

  buildActivityWeekStyle(activity: any, weekGroup: CalendarWeekGroup): string {
    if (!this.isActivityInWeek(activity, weekGroup)) {
      return '';
    }

    const staticStyle = '; height: 100%; ';

    if (activity.IsProjectLevel) {
      const isOnHold = !!(activity.IsOnHold ?? activity.ProjectIsOnHold);
      const statusColor = isOnHold ? getStatusColor(5) : activity.ProjectHasShipped ? shippedColor : getStatusColor(activity.ProjectStatusId);
      return 'background: ' + statusColor + staticStyle;
    }

    const lightColor = this.getResponsiblePartyColor(activity.ResponsiblePartyId, true);
    const darkColor = this.getResponsiblePartyColor(activity.ResponsiblePartyId, false);

    if (!activity.IsConfirmed) {
      return 'background: ' + lightColor + staticStyle;
    }

    const weekStart = weekGroup.dates[0];
    const weekEnd = weekGroup.dates[weekGroup.dates.length - 1];
    let topColor = '#00000000';
    let bottomColor = '#00000000';

    const expectedEndDate = new Date(activity.ExpectedEndDate);
    const actualEndDate = activity.ActualEndDate ? new Date(activity.ActualEndDate) : expectedEndDate;

    // Only the week(s) spanning the overrun (between expected end and actual end) should show the
    // late/warning overlay; weeks entirely before or after that overrun stay the normal color.
    if (weekEnd >= expectedEndDate && weekStart <= actualEndDate && actualEndDate > expectedEndDate) {
      topColor = this.isWarning(activity, actualEndDate) ? '#fff126ff' : '#FF0000';
    } else {
      topColor = lightColor;
    }

    if (activity.ActualStartDate && activity.ActualEndDate &&
      weekStart <= new Date(activity.ActualEndDate) && weekEnd >= new Date(activity.ActualStartDate)) {
      bottomColor = darkColor;
    }

    return `background: linear-gradient(
      to bottom,
      ${topColor} 0%,
      ${topColor} 33.33%,
      ${bottomColor} 33.33%,
      ${bottomColor} 100% /*
    )` + staticStyle;
  }

  isWeekView(): boolean {
    return this.filterSettings.showWeeks;
  }

  isFirstOfMonth(date: Date): boolean {
    return date.getDate() === 1;
  }

  isWorkDay(activity: any, date: Date): boolean {
    const day = date.getDay();
    return day === 1 || day === 2 || day === 3 || day === 4 ||
      ((activity === null || activity.ResponsiblePartyId !== 3) && day === 5);
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();
  }

  isBeforeToday(date: Date): boolean {
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  }

  isHoliday(activity: any, date: Date): boolean {
    return this.holidays.some(holiday =>
      holiday.ResponsiblePartyId === activity.ResponsiblePartyId &&
      holiday.Date.getFullYear() === date.getFullYear() &&
      holiday.Date.getMonth() === date.getMonth() &&
      holiday.Date.getDate() === date.getDate()
    );
  }

  isVisible(activity: any): boolean {
    if (this.filterSettings.showOnlyMilestones && !activity.IsMilestone) return false;
    if (!this.filterSettings.showConfirmed && activity.IsConfirmed) return false;
    if (!this.filterSettings.showUnconfirmed && !activity.IsConfirmed) return false;
    if (!this.filterSettings.showEarly && activity.StatusId === 1) return false;
    if (!this.filterSettings.showOnTime && activity.StatusId === 2) return false;
    if (!this.filterSettings.showInDanger && activity.StatusId === 3) return false;
    if (!this.filterSettings.showOnHold && activity.StatusId === 5) return false;
    if (!this.filterSettings.showLate && activity.StatusId === 4) return false;
    if (!this.filterSettings.showCustomer && activity.ResponsiblePartyId === 0) return false;
    if (!this.filterSettings.showSales && activity.ResponsiblePartyId === 1) return false;
    if (!this.filterSettings.showEngineering && activity.ResponsiblePartyId === 2) return false;
    if (!this.filterSettings.showProduction && activity.ResponsiblePartyId === 3) return false;
    return true;
  }

  isWarning(activity: any, date: Date): boolean {
    let daysLate = 0;
    if (date > new Date(activity.ExpectedEndDate)) {
      let currentDate = new Date(activity.ExpectedEndDate);
      while (currentDate < date) {
        currentDate.setDate(currentDate.getDate() + 1);
        if (currentDate >= date) {
          break;
        }
        if (!this.isWorkDay(activity, currentDate) || this.isHoliday(activity, currentDate)) {
          continue;
        }
        daysLate++;
      }
    }
    return daysLate < activity.WarningThresholdDays;
  }


  buildActivityStyle(activity: any, date: Date): string {
    if (!this.isActivityOnDate(activity, date) ||
      this.isSelectedDay(activity, date)) {
      return '';
    }
    return this.buildActivityCellStyle(activity, date);
  }

  trackByDate(_index: number, dayColumn: CalendarDayColumn): number {
    return dayColumn.date.getTime();
  }

  trackByDayCellDate(_index: number, dayCell: CalendarDayCell): number {
    return dayCell.date.getTime();
  }

  trackByActivityId(_index: number, activity: any): number {
    return activity.Id;
  }

  trackByWeekGroup(_index: number, weekGroup: CalendarWeekGroup): string {
    return `${weekGroup.year}-${weekGroup.weekNumber}`;
  }

  trackByYearGroup(_index: number, yearGroup: CalendarYearGroup): string {
    return `${yearGroup.year}-${yearGroup.span}`;
  }

  isSelectedDay(activity: any, date: Date): boolean {
    return activity.Id === this.selectionRowId &&
      this.selectionStartDate !== null &&
      this.selectionEndDate !== null &&
      date >= this.selectionStartDate &&
      date <= this.selectionEndDate;
  }

  isNoteIconDate(activity: any, date: Date): boolean {
    let endDate = activity?.ActualEndDate ?? activity?.ExpectedEndDate;
    if (!endDate) return false;
    
    endDate = new Date(endDate);
    endDate.setDate(endDate.getDate() + 1);
    return endDate &&
      date.getFullYear() === endDate.getFullYear() &&
      date.getMonth() === endDate.getMonth() &&
      date.getDate() === endDate.getDate();
  }

  onCellMouseDown(event: MouseEvent, activity: any, date: Date) {
    if (!this.canEdit) {
      return;
    }
    if (!activity.IsMilestone && !activity.IsLocked && event.button === 0) {
      this.selectionRowId = activity.Id;
      this.selectionStartDate = date;
      this.selectionEndDate = date;
    }
    if (event.button === 2 && this.selectionRowId) {
      this.selectionRowId = null;
      this.selectionStartDate = null;
      this.selectionEndDate = null;
    }
  }

  onCellMouseOver(event: MouseEvent, activity: any, date: Date) {
    if (this.selectionStartDate) {
      this.selectionEndDate = date;
    }
  }

  onCellMouseUp(event: MouseEvent, activity: any, date: Date) {
    if (event.button !== 0) {
      return; // only respond to left mouse button
    }
    if (this.selectionStartDate) {
      this.selectionEndDate = date;

      let selectedActivity = this.activities.find(a => a.Id === this.selectionRowId);
      if (selectedActivity) {
        if (selectedActivity.IsConfirmed) {
          if (!selectedActivity.ExpectedStartDate)
            selectedActivity.ExpectedStartDate = this.selectionStartDate;
          if (!selectedActivity.ExpectedEndDate)
            selectedActivity.ExpectedEndDate = this.selectionEndDate;
          if ((!selectedActivity.ActualStartDate || this.selectionStartDate !== this.selectionEndDate) || !event.shiftKey)
            selectedActivity.ActualStartDate = this.selectionStartDate;
          selectedActivity.ActualEndDate = this.selectionEndDate;
        } else {
          if ((!selectedActivity.ExpectedStartDate || this.selectionStartDate !== this.selectionEndDate) || !event.shiftKey) {
            selectedActivity.ExpectedStartDate = this.selectionStartDate;
          }
          selectedActivity.ExpectedEndDate = this.selectionEndDate;
        }
        this.updateActivity(selectedActivity);
      }

      this.selectionRowId = null;
      this.selectionStartDate = null;
      this.selectionEndDate = null;
    }
  }

  onCellMouseWheel(event: WheelEvent, activity: any, date: Date) {
    event.preventDefault();
    const delta = Math.sign(event.deltaY);
    if (delta > 0) {
      // Scrolling left
      this.filterSettings.selectedDay = Math.min(this.filterSettings.selectedDay + delta * 5, this.filterSettings.dayRangeMax);
    } else if (delta < 0) {
      // Scrolling right
      this.filterSettings.selectedDay = Math.max(this.filterSettings.selectedDay + delta * 5, this.filterSettings.dayRangeMin);
    }
    this.dayRangeChanged();
  }

  // Zoom controls
  zoomIn() {
    this.setZoom(this.zoomLevel + 0.1);
  }

  zoomOut() {
    this.setZoom(this.zoomLevel - 0.1);
  }

  onZoomRange(event: any) {
    const value = Number(event.target.value) || 100;
    this.setZoom(value / 100);
  }

  private setZoom(value: number) {
    const nextZoomLevel = Math.max(0.5, Math.min(2.0, Math.round(value * 100) / 100));
    if (this.zoomLevel === nextZoomLevel) {
      return;
    }

    this.zoomLevel = nextZoomLevel;
    // Keep transform origin at the top-left so headers remain visible
    this.zoomOrigin = '0 0';

    if (!this.filterSettings.showWeeks) {
      this.recalculateDaysShownFromTableWidth();
      this.dayRangeChanged();
    }

    this.scheduleViewportHeightUpdate();
  }


  openContextMenu(event: MouseEvent, activity: any, date: Date) {
    event.preventDefault();
    this.contextRowId = activity.Id;
    this.contextMenuVisible = true;
    this.contextMenuPosition.x = event.clientX + 6;
    this.contextMenuPosition.y = event.clientY + 6;
    this.contextActivity = activity;
  }

  closeContextMenu() {
    this.contextMenuVisible = false;
    this.contextRowId = null;
  }

  canOverrideExpectedDate(activity: any): boolean {
    return this.canEdit && !!activity && activity.ResponsiblePartyId === 3 && activity.IsLocked;
  }

  getSelectedActivity(): any | null {
    if (this.contextRowId === null) {
      return null;
    }
    return this.activities.find(a => a.Id === this.contextRowId);
  }

  confirmRange() {
    let selectedActivity = this.getSelectedActivity();
    if (selectedActivity) {
      selectedActivity.ActualStartDate = selectedActivity.ExpectedStartDate;
      selectedActivity.ActualEndDate = selectedActivity.ExpectedEndDate;
      selectedActivity.IsConfirmed = true;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  unconfirmRange() {
    let selectedActivity = this.getSelectedActivity();
    if (selectedActivity) {
      selectedActivity.ActualStartDate = null;
      selectedActivity.ActualEndDate = null;
      selectedActivity.IsConfirmed = false;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  confirmAllRanges() {
    let selectedActivity = this.getSelectedActivity();
    let childActivities = this.activities.filter(a => a.MilestoneActivityId === selectedActivity.Id);
    for (let activity of childActivities) {
      activity.ActualStartDate = activity.ExpectedStartDate;
      activity.ActualEndDate = activity.ExpectedEndDate;
      activity.IsConfirmed = true;
    }
    this.updateActivityBatch(childActivities, "Confirm All");
    this.closeContextMenu();
  }

  completeRange() {
    let selectedActivity = this.getSelectedActivity();
    if (selectedActivity) {
      selectedActivity.IsCompleted = true;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  uncompleteRange() {
    let selectedActivity = this.getSelectedActivity();
    if (selectedActivity) {
      selectedActivity.IsCompleted = false;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  resetExpectedRange() {
    let selectedActivity = this.getSelectedActivity();
    if (selectedActivity) {
      selectedActivity.ExpectedStartDate = selectedActivity.ActualStartDate;
      selectedActivity.ExpectedEndDate = selectedActivity.ActualEndDate;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  deleteRange() {
    let selectedActivity = this.activities.find(a => a.Id === this.contextRowId);
    if (selectedActivity && !selectedActivity.IsMilestone) {
      selectedActivity.ExpectedStartDate = undefined;
      selectedActivity.ExpectedEndDate = undefined;
      selectedActivity.ActualStartDate = undefined;
      selectedActivity.ActualEndDate = undefined;
      selectedActivity.IsConfirmed = false;
      this.updateActivity(selectedActivity);
    }
    this.closeContextMenu();
  }

  showSelectedHistory() {
    this.openActivityHistory(this.getSelectedActivity(), new MouseEvent('click'));
    this.closeContextMenu();
  }

  openOverrideExpectedDateDialog() {
    const selectedActivity = this.getSelectedActivity();
    if (!this.canOverrideExpectedDate(selectedActivity)) {
      this.closeContextMenu();
      return;
    }

    this.overrideExpectedDateActivity = selectedActivity;
    this.overrideExpectedStartDate = this.formatDateInputValue(selectedActivity.ExpectedStartDate);
    this.overrideExpectedEndDate = this.formatDateInputValue(selectedActivity.ExpectedEndDate);
    this.overrideExpectedDateComment = '';
    this.overrideExpectedDateVisible = true;
    this.closeContextMenu();
  }

  closeOverrideExpectedDateDialog() {
    this.overrideExpectedDateVisible = false;
    this.overrideExpectedDateActivity = null;
    this.overrideExpectedStartDate = '';
    this.overrideExpectedEndDate = '';
    this.overrideExpectedDateComment = '';
  }

  canSubmitOverrideExpectedDate(): boolean {
    return !!this.overrideExpectedDateActivity &&
      !!this.overrideExpectedStartDate &&
      !!this.overrideExpectedEndDate &&
      !!this.overrideExpectedDateComment.trim();
  }

  submitOverrideExpectedDate() {
    if (!this.canSubmitOverrideExpectedDate()) {
      return;
    }

    const selectedActivity = this.overrideExpectedDateActivity;
    const hadExpectedDatesOverride = !!selectedActivity?.HasExpectedDatesOverride;
    const expectedStartDate = this.parseDateInputValue(this.overrideExpectedStartDate);
    const expectedEndDate = this.parseDateInputValue(this.overrideExpectedEndDate);
    if (!selectedActivity || !expectedStartDate || !expectedEndDate) {
      return;
    }

    selectedActivity.HasExpectedDatesOverride = true;
    selectedActivity.ExpectedStartDate = expectedStartDate;
    selectedActivity.ExpectedEndDate = expectedEndDate;
    this.updateActivity(selectedActivity, this.overrideExpectedDateComment.trim(), () => {
      if (!hadExpectedDatesOverride) {
        this.projectData.HasExpectedDatesOverride = (this.projectData?.HasExpectedDatesOverride ?? 0) + 1;
      }
      this.api.MasterSchedule.InsertMasterScheduleActivityNote(selectedActivity.Id, 'Production Override: ' + this.overrideExpectedDateComment.trim())
        .subscribe(() => {
          selectedActivity.NoteCount = (selectedActivity.NoteCount ?? 0) + 1;
          this.projectData.NoteCount = (this.projectData.NoteCount ?? 0) + 1;
        });
      this.closeOverrideExpectedDateDialog();
    });
  }

  formatDateInputValue(date: Date | string | null | undefined): string {
    if (!date) return '';
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return '';

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  parseDateInputValue(value: string): Date | null {
    if (!value) return null;

    const parsedDate = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  openDuplicateDatesDialog() {
    const selectedActivity = this.getSelectedActivity();
    if (!selectedActivity || selectedActivity.IsLocked || !this.canEdit) {
      this.closeContextMenu();
      return;
    }

    if (selectedActivity.IsMilestone) {
      this.duplicateDatesActivityIds = this.activities
        .filter(activity => activity.MilestoneActivityId === selectedActivity.Id)
        .map(activity => activity.Id);
      if (this.duplicateDatesActivityIds.length === 0) {
        this.closeContextMenu();
        return;
      }
    } else {
      this.duplicateDatesActivityIds = [selectedActivity.Id];
    }

    this.duplicateDatesVisible = true;
    this.duplicateDatesSelectedProjectIds = [];

    this.closeContextMenu();
  }

  closeDuplicateDatesDialog() {
    this.duplicateDatesVisible = false;
    this.duplicateDatesActivityIds = [];
    this.duplicateDatesSelectedProjectIds = [];
  }

  duplicateDates() {
    if (this.duplicateDatesActivityIds.length === 0 || this.duplicateDatesSelectedProjectIds.length === 0) {
      return;
    }

    if (this.duplicateDatesSelectedProjectIds.length > this.duplicateDatesSelectionLimit) {
      this.duplicateDatesSelectedProjectIds = this.duplicateDatesSelectedProjectIds.slice(0, this.duplicateDatesSelectionLimit);
    }

    this.updating = true;
    const duplicateRequests = this.duplicateDatesActivityIds
      .flatMap(activityId => this.duplicateDatesSelectedProjectIds
        .map(projectId => this.api.MasterSchedule.DuplicateMasterScheduleActivityDates(activityId, projectId)));

    forkJoin(duplicateRequests)
      .subscribe(() => {
        this.updating = false;
        this.lastBackendUpdate = new Date();
        this.backendCheckVisible = false;
        this.closeDuplicateDatesDialog();
        this.updateProjects?.();
      });
  }

  updateActivity(activity: any, comment: string = '', postUpdateCallback: (() => void) | null = null) {
    this.refreshRenderedActivities();
    this.scheduleViewportHeightUpdate();
    this.updating = true;
    // over 20 min since last backend update, show backend check indicator
    if ((new Date().getTime() - (this.lastBackendUpdate?.getTime() ?? 0)) > 20 * 60 * 1000) {
      this.backendCheckVisible = true;
    }
    this.api.MasterSchedule.UpdateMasterScheduleActivity(activity, comment).subscribe(updatedActivities => {
      for (let updatedActivity of updatedActivities) {
        let localActivity = this.activities.find(a => a.Id === updatedActivity.Id);
        if (localActivity) {
          localActivity.ExpectedStartDate = new Date(updatedActivity.ExpectedStartDate);
          localActivity.ExpectedEndDate = new Date(updatedActivity.ExpectedEndDate);
          localActivity.ActualStartDate = new Date(updatedActivity.ActualStartDate);
          localActivity.ActualEndDate = new Date(updatedActivity.ActualEndDate);
          localActivity.IsConfirmed = updatedActivity.IsConfirmed;
          localActivity.StatusId = updatedActivity.StatusId;
        }
      }
      this.updating = false;
      this.lastBackendUpdate = new Date();
      this.backendCheckVisible = false;
      this.refreshRenderedActivities();
      this.scheduleViewportHeightUpdate();
      this.updateProjects?.();
      postUpdateCallback?.();
    });
  }

  updateActivityBatch(activities: any[], comment: string = "Batch Update") {
    this.refreshRenderedActivities();
    this.scheduleViewportHeightUpdate();
    this.updating = true;
    this.api.MasterSchedule.UpdateMasterScheduleActivityBatch(activities, comment).subscribe(updatedActivities => {
      for (let updatedActivity of updatedActivities) {
        let localActivity = this.activities.find(a => a.Id === updatedActivity.Id);
        if (localActivity) {
          localActivity.ExpectedStartDate = new Date(updatedActivity.ExpectedStartDate);
          localActivity.ExpectedEndDate = new Date(updatedActivity.ExpectedEndDate);
          localActivity.ActualStartDate = new Date(updatedActivity.ActualStartDate);
          localActivity.ActualEndDate = new Date(updatedActivity.ActualEndDate);
          localActivity.IsConfirmed = updatedActivity.IsConfirmed;
          localActivity.StatusId = updatedActivity.StatusId;
        }
      }
      this.updating = false;
      this.lastBackendUpdate = new Date();
      this.backendCheckVisible = false;
      this.refreshRenderedActivities();
      this.scheduleViewportHeightUpdate();
      this.updateProjects?.();
    })
  };

  openActivityHistory(activity: any, event: MouseEvent) {
    event.stopPropagation();
    this.api.MasterSchedule.GetMasterScheduleActivityRevisions(activity.Id).subscribe(revisions => {
      this.activityHistoryData = revisions;
      this.activityHistoryVisible = true;
    });
  }

  closeActivityHistory() {
    this.activityHistoryVisible = false;
    this.activityHistoryData = [];
  }

  toBrowserDate(timestamp: string | Date | null | undefined): Date | null {
    if (!timestamp) {
      return null;
    }

    if (timestamp instanceof Date) {
      return timestamp;
    }

    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
    const date = new Date(hasTimezone ? timestamp : `${timestamp}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  openDateShifter(event: MouseEvent) {
    event.preventDefault();
    this.dateShifterVisible = true;
    this.dateShifterPosition.x = event.clientX;
    this.dateShifterPosition.y = event.clientY;
    this.dateShiftDays = 1;
  }

  toggleDateShifterActivitySelection(activity: any) {
    if (this.dateShifterActivities.includes(activity)) {
      this.dateShifterActivities = this.dateShifterActivities.filter(a => a.Id !== activity.Id);
    } else {
      this.dateShifterActivities.push(activity);
    }
  }

  applyDateShift() {
    for (let activity of this.dateShifterActivities) {
      let startDate = activity.ExpectedStartDate;
      let endDate = activity.ExpectedEndDate;
      let shift = this.dateShiftDays > 0 ? 1 : -1;
      for (let i = 0; i < Math.abs(this.dateShiftDays); i++) {
        do {
          startDate.setDate(startDate.getDate() + shift);
        } while (!this.isWorkDay(activity, startDate) && !this.isHoliday(activity, startDate));
        do {
          endDate.setDate(endDate.getDate() + shift);
        } while (!this.isWorkDay(activity, endDate) && !this.isHoliday(activity, endDate));
      }
      activity.ExpectedStartDate = startDate;
      activity.ExpectedEndDate = endDate;
    }

    this.updateActivityBatch(this.dateShifterActivities, "Date Shift");
    this.dateShifterActivities = [];
    this.dateShifterVisible = false;
  }

  closeDateShifter() {
    this.dateShifterVisible = false;
  }

  cleanDescription(description: string): string {
    if (!description) return '';
    return description.replace(/^[0-9]+ - /, '');
  }

  openFilterMenu(event: MouseEvent) {
    this.filterMenuPosition.x = event.clientX - 800 + 10;
    this.filterMenuPosition.y = event.clientY - 10;
    this.filterMenuVisible = true;
  }

  closeFilterMenu() {
    this.filterMenuVisible = false;
  }

  setAllView() {
    this.filterSettings.showWeekends = true;
    this.filterSettings.showFridays = true;
    this.filterSettings.showCustomer = true;
    this.filterSettings.showSales = true;
    this.filterSettings.showEngineering = true;
    this.filterSettings.showProduction = true;
    this.dayRangeChanged();
  }

  setSalesView() {
    this.filterSettings.showWeekends = false;
    this.filterSettings.showFridays = true;
    this.filterSettings.showCustomer = true;
    this.filterSettings.showSales = true;
    this.filterSettings.showEngineering = false;
    this.filterSettings.showProduction = false;
    this.dayRangeChanged();
  }

  setEngineeringView() {
    this.filterSettings.showWeekends = false;
    this.filterSettings.showFridays = true;
    this.filterSettings.showCustomer = false;
    this.filterSettings.showSales = false;
    this.filterSettings.showEngineering = true;
    this.filterSettings.showProduction = false;
    this.dayRangeChanged();
  }

  setProductionView() {
    this.filterSettings.showWeekends = false;
    this.filterSettings.showFridays = false;
    this.filterSettings.showCustomer = false;
    this.filterSettings.showSales = false;
    this.filterSettings.showEngineering = false;
    this.filterSettings.showProduction = true;
    this.dayRangeChanged();
  }

  openInNewTab(projectNumber: string) {
    const url = `${window.location.origin}/#/master-schedule-single-project;project=${projectNumber}`;
    window.open(url, '_blank');
  }

  openProjectModal(projectNumber: string) {
    if (!this.projectNumberClickEnabled) {
      return;
    }

    this.singleProjectModalProject = projectNumber;
    this.singleProjectModalVisible = true;
  }

  openProjectNumber(projectNumber: string) {
    this.openProjectModal(projectNumber);
  }

  closeProjectModal() {
    this.singleProjectModalVisible = false;
    this.singleProjectModalProject = '';
  }

  openNotes(activity: any) {
    this.notesActivity = activity ?? null;
    if (!this.notesActivity) return;

    this.showNotes = true;
    this.notes = [];
    this.refreshNotesData();
  }

  addNote(newNote: string) {
    if (newNote == '') return;
    this.api.MasterSchedule.InsertMasterScheduleActivityNote(this.notesActivity.Id, newNote).subscribe((x) => {
      this.refreshNotesData();
      this.notesActivity.NoteCount = (this.notesActivity.NoteCount ?? 0) + 1;
      if (this.projectData) {
        this.projectData.NoteCount = (this.projectData.NoteCount ?? 0) + 1;
      }
      this.updateProjects?.();
    });
  }

  refreshNotesData() {
    this.api.MasterSchedule.GetMasterScheduleActivityNotes(this.notesActivity.Id).subscribe((x) => {
      this.notes = [];
      this.notes = x;
      this.notes = this.notes.reverse();
    });
  }

  getPageNumbers(): number[] {
    let pageNumbers: number[] = [];
    for (let i = 1; i <= this.pages; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  }

  // The backend pages by activity, so pager totals are scaled down by the activities selected per project.
  getActivitiesPerProject(): number {
    const selectedActivities = (this.filterSettings.activity ?? '')
      .split(';')
      .filter(activity => activity.trim().length > 0);
    return Math.max(1, selectedActivities.length);
  }

  getPagerStart(): number {
    return Math.floor(((this.page - 1) * this.pageSize) / this.getActivitiesPerProject()) + 1;
  }

  getPagerEnd(): number {
    const projectsOnPage = new Set(this.activities.map(activity => activity.ProjectNumber)).size;
    return this.getPagerStart() + Math.max(0, projectsOnPage - 1);
  }

  getProjectCount(): number {
    return Math.ceil(this.count / this.getActivitiesPerProject());
  }

}
  